/**
 * Generic cache entry that represents either an in-flight request or a cached response.
 */
export type CacheEntry<TResponse> =
  | {
      state: "active";
      request: Promise<TResponse>;
    }
  | {
      state: "cached";
      response: TResponse;
      succeededAt: number;
    };

/**
 * ApiRequestCachingStore provides generic caching and request deduplication.
 *
 * Features:
 * - Prevents duplicate concurrent API calls (in-flight deduplication).
 * - Caches successful responses for a configurable TTL.
 * - Supports cache invalidation and clearing.
 * - Reusable by any service that needs HTTP response caching.
 */
export class ApiRequestCachingStore {
  private items = new Map<string, CacheEntry<unknown>>();

  /**
   * Get a cached response or execute a request with automatic caching.
   *
   * @param key - Unique cache key (e.g., "logo-en", "user-profile-123").
   * @param request - Async function that fetches the data.
   * @param cacheTtl - Time-to-live in milliseconds. Data older than this is considered expired.
   * @returns The cached response or the result of the new request.
   *
   * Behavior:
   * 1. If an identical request is already in flight, returns the same promise (deduplication).
   * 2. If a valid cached response exists (not expired), returns it immediately.
   * 3. Otherwise, executes the request, caches the response, and returns it.
   */
  public async getCachedOrRequest<TResponse>(
    key: string,
    request: () => Promise<TResponse>,
    cacheTtl: number,
  ): Promise<TResponse> {
    const entry = this.items.get(key);

    // Return is currently active request if one exists (deduplication)
    if (entry && entry.state === "active") {
      return entry.request as Promise<TResponse>;
    }

    // Return cached response if not expired
    if (entry && entry.state === "cached") {
      if (entry.succeededAt + cacheTtl > Date.now()) {
        return entry.response as TResponse;
      }
      // Cache expired, remove it
      this.items.delete(key);
    }

    // Execute new request
    const wrappedRequest = request()
      .then(response => {
        // Store successful response in cache
        this.items.set(key, {
          state: "cached",
          response,
          succeededAt: Date.now(),
        });

        return response;
      })
      .catch(error => {
        // Request failed, remove active cache entry
        this.items.delete(key);
        console.error(`ApiRequestCachingStore request failed for key '${key}'`, error);

        throw error;
      });

    // Store the in-flight request
    this.items.set(key, {
      state: "active",
      request: wrappedRequest,
    });

    return wrappedRequest;
  }

  /**
   * Remove a cached entry and any in-flight request associated with the key.
   * Useful after data mutations (e.g., logo upload) to force a fresh fetch next time.
   */
  public remove(key: string): void {
    this.items.delete(key);
  }

  /**
   * Clear all cached entries and in-flight requests.
   */
  public clear(): void {
    this.items.clear();
  }
}
